import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import classes from './CreatePerkForm.module.css';
import { MerchantCategories } from '~/enums/MerchantCategories';
import { uriRegex } from '~/helpers/helpers';
import { SelfServePerkForm } from '~/types/perk';
import { Merchant } from '~/types/merchant';

interface IProps {
  onSubmit: (data: SelfServePerkForm) => void;
  onCancel: () => void;
  submitting: boolean;
  buildingMerchants: Merchant[];
}

const today = new Date().toISOString().split('T')[0];

const formatExpiry = (value: string) =>
  moment(value, 'YYYY-MM-DD', true).format('dddd, D MMMM YYYY');

export const CreatePerkForm = (props: IProps) => {
  const { onSubmit, onCancel, submitting, buildingMerchants } = props;

  const { register, handleSubmit, errors, watch, setValue } =
    useForm<SelfServePerkForm>({
      defaultValues: {
        businessName: '',
        category: '',
        offerName: '',
        offerType: 'redeem',
        redeemLink: '',
        description: '',
        expiresAt: ''
      }
    });

  const expiresAt = watch('expiresAt');

  // Same case-insensitive match the backend uses to decide whether to reuse a
  // merchant. Category lives on the merchant, so picking a different one here
  // changes it for every offer that business already has.
  const businessName = watch('businessName');
  const category = watch('category');
  const existingMerchant = buildingMerchants.find(
    (merchant) =>
      merchant.name?.trim().toLowerCase() === businessName?.trim().toLowerCase()
  );
  const categoryWillChange =
    !!existingMerchant && !!category && existingMerchant.category !== category;

  // Fill in the stored category once the typed name matches a business, so the
  // common case needs no choice. Tracked rather than keyed off "is the dropdown
  // empty": retyping the name to a different business must replace a value this
  // effect set, but never one the PM chose.
  const autoFilled = useRef(false);
  const matchedCategory = existingMerchant?.category;
  useEffect(() => {
    if (!matchedCategory) return;
    if (!category || autoFilled.current) {
      autoFilled.current = true;
      setValue('category', matchedCategory);
    }
  }, [matchedCategory, category, setValue]);

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.field}>
        <label htmlFor='businessName'>Business name *</label>
        <input
          type='text'
          id='businessName'
          name='businessName'
          placeholder="e.g. Joe's Coffee"
          ref={register({ required: 'Business name is required' })}
          className={errors.businessName ? 'border-red' : ''}
        />
        {errors.businessName && (
          <span className={classes.error}>{errors.businessName.message}</span>
        )}
      </div>

      <div className={classes.field}>
        <label htmlFor='category'>Category *</label>
        <select
          id='category'
          name='category'
          onChange={() => {
            autoFilled.current = false;
          }}
          ref={register({ required: 'Category is required' })}
          className={`height-50 ${errors.category ? 'border-red' : ''}`}
        >
          <option value=''>Choose category</option>
          {MerchantCategories.map((option: string) => (
            <option value={option} key={option}>
              {option}
            </option>
          ))}
        </select>
        {errors.category && (
          <span className={classes.error}>{errors.category.message}</span>
        )}
        {categoryWillChange && existingMerchant && (
          <span className={classes.warning}>
            {`${existingMerchant.name} is currently listed as ` +
              `${existingMerchant.category}. Publishing will change it ` +
              'for all of their offers.'}
          </span>
        )}
      </div>

      <div className={classes.field}>
        <label htmlFor='offerName'>Offer *</label>
        <input
          type='text'
          id='offerName'
          name='offerName'
          placeholder='e.g. 15% off your purchase'
          maxLength={50}
          ref={register({
            required: 'Offer is required',
            maxLength: {
              value: 50,
              message: 'Keep the offer to 50 characters or fewer.'
            }
          })}
          className={errors.offerName ? 'border-red' : ''}
        />
        {errors.offerName && (
          <span className={classes.error}>{errors.offerName.message}</span>
        )}
      </div>

      <div className={classes.field}>
        <label htmlFor='offerType'>Button</label>
        <select
          id='offerType'
          name='offerType'
          ref={register}
          className='height-50'
        >
          <option value='redeem'>Redeem</option>
          <option value='learn'>Learn More</option>
        </select>
      </div>

      <div className={classes.field}>
        <label htmlFor='redeemLink'>Redeem link *</label>
        <input
          type='text'
          id='redeemLink'
          name='redeemLink'
          placeholder='https://...'
          ref={register({
            required: 'A redeem link is required',
            pattern: {
              value: uriRegex(),
              message: 'Please enter a valid URL'
            }
          })}
          className={errors.redeemLink ? 'border-red' : ''}
        />
        {errors.redeemLink && (
          <span className={classes.error}>{errors.redeemLink.message}</span>
        )}
      </div>

      <div className={classes.field}>
        <label htmlFor='description'>Description</label>
        <textarea
          id='description'
          name='description'
          placeholder='e.g. Show your Cobu app at the register. Add any other details residents should know.'
          ref={register}
        />
      </div>

      <div className={classes.field}>
        <label htmlFor='expiresAt'>Expiration date</label>
        <input
          type='date'
          id='expiresAt'
          name='expiresAt'
          min={today}
          ref={register}
        />
        <span className={classes.hint}>
          {expiresAt
            ? `Residents can see this perk through ${formatExpiry(
                expiresAt
              )}. It stops showing the next day.`
            : 'Leave blank and the perk never expires.'}
        </span>
      </div>

      <div className={classes.buttons}>
        <button
          className='btn-standard btn-secondary'
          type='button'
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          className='btn-standard btn-primary margin-left-16'
          type='submit'
          disabled={submitting}
        >
          {submitting ? 'Publishing...' : 'Publish Perk'}
        </button>
      </div>
    </form>
  );
};

export default CreatePerkForm;
